import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api, ErrorDeApi, type Cliente, type DatosDelPapelDeIsr, type PapelDeIsr as Papel, type PapelDeIsrResumen,
} from '../servicios/api'
import { nombreDePeriodo, periodosDisponibles } from '../periodos'
import { ParametrosDeIsr } from '../componentes/ParametrosDeIsr'
import { PaqueteDeDeclaraciones } from '../componentes/PaqueteDeDeclaraciones'

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** `generadoEnUtc` es un instante: se convierte a hora local (ver fechas.ts). */
const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

/** Los renglones que cierran el cálculo van resaltados: son los que se llevan a la declaración. */
const RENGLONES_DE_RESULTADO = new Set(['BaseDelPagoProvisional', 'ImpuestoDelPeriodo', 'ImpuestoAPagar'])

const mensaje = (e: unknown, respaldo: string) => (e instanceof ErrorDeApi ? e.message : respaldo)

/**
 * Lo que el contador captura en el mes, cada cifra con su origen. El coeficiente, la PTU y las
 * pérdidas se capturan aparte, en los parámetros del ejercicio, que rigen por tramos (RF-1208).
 */
const CIFRAS = [
  { campo: 'pagosAnteriores', origen: 'origenDePagosAnteriores', etiqueta: 'Pagos provisionales anteriores efectivamente enterados (netos de retenciones)',
    etiquetaDelOrigen: 'De dónde salen los pagos', ejemplo: 'Declaraciones de enero y febrero de 2026' },
  { campo: 'retenciones', origen: 'origenDeRetenciones', etiqueta: 'Retenciones acumuladas del ejercicio (art. 54)',
    etiquetaDelOrigen: 'De dónde salen las retenciones', ejemplo: 'Constancia de retención del banco' },
] as const

/** Con cambios sin guardar en los parámetros, el papel saldría con los guardados mientras se ven otros. */
const AVISO_DE_PARAMETROS_SUCIOS = 'Guarda o descarta los cambios de los parámetros del ejercicio antes de calcular o sellar.'

type Campos = Record<(typeof CIFRAS)[number]['campo'] | (typeof CIFRAS)[number]['origen'], string>

const VACIOS: Campos = { pagosAnteriores: '', origenDePagosAnteriores: '', retenciones: '', origenDeRetenciones: '' }

/** Un campo vacío no se manda: el servidor lo toma por cero y el papel lo dice en el origen del renglón. */
function adatos(campos: Campos): DatosDelPapelDeIsr {
  const datos: Record<string, number | string> = {}
  for (const { campo, origen } of CIFRAS) {
    if (campos[campo].trim() !== '') datos[campo] = Number(campos[campo])
    if (campos[origen].trim() !== '') datos[origen] = campos[origen].trim()
  }
  return datos as DatosDelPapelDeIsr
}

/**
 * Papel de trabajo del pago provisional de ISR de personas morales (RF-1206). Los ingresos salen
 * de la contabilidad; el coeficiente, la PTU y las pérdidas, de los parámetros del ejercicio
 * (RF-1208); los pagos anteriores y las retenciones los capturas en el mes con su origen. Sellar lo
 * congela tal como se vio, con el origen de cada importe y la tasa usada (RN-32). Abajo quedan los
 * sellados del mes, el más reciente primero.
 */
export function PapelDeIsr() {
  const { id: clienteId } = useParams<{ id: string }>()
  const hoy = new Date()
  const periodos = useMemo(() => periodosDisponibles(hoy), [hoy.getFullYear(), hoy.getMonth()])
  const [anio, setAnio] = useState(periodos[1].anio)
  const [mes, setMes] = useState(periodos[1].mes)
  const [cliente, setCliente] = useState<Cliente>()
  const [campos, setCampos] = useState<Campos>(VACIOS)
  const [borrador, setBorrador] = useState<Papel>()
  const [calculadoCon, setCalculadoCon] = useState<DatosDelPapelDeIsr>()
  const [errorBorrador, setErrorBorrador] = useState('')
  const [papeles, setPapeles] = useState<PapelDeIsrResumen[]>([])
  const [viendo, setViendo] = useState<Papel>()
  const [ocupado, setOcupado] = useState(false)
  const [errorAccion, setErrorAccion] = useState('')
  const [errorDeLista, setErrorDeLista] = useState('')
  const [parametrosSucios, setParametrosSucios] = useState(false)
  // Cambiarla vuelve a montar el panel de parámetros: así relee los vigentes cuando el sello dice que cambiaron.
  const [recargaDeParametros, setRecargaDeParametros] = useState(0)

  // El periodo que se está viendo ahora: una respuesta que llega de otro mes ya no es de esta pantalla.
  const periodoVigente = useRef({ anio, mes })
  const sigueEnElPeriodo = (a: number, m: number) => periodoVigente.current.anio === a && periodoVigente.current.mes === m

  // Cada cálculo pedido lleva su número, y cambiar de mes o de parámetros lo invalida: un borrador que
  // llega después se calculó con lo de antes y ya no es lo que quedaría sellado.
  const generacion = useRef(0)

  useEffect(() => {
    if (!clienteId) return
    api.obtenerCliente(clienteId).then(setCliente).catch(() => setCliente(undefined))
  }, [clienteId])

  // Al cambiar de mes el borrador se descarta (era de otro mes) y también lo capturado: los pagos
  // anteriores y las retenciones acumuladas cambian cada mes. Lo que rige por tramos del ejercicio vive en
  // los parámetros, que se recargan solos al cambiar de ejercicio.
  useEffect(() => {
    periodoVigente.current = { anio, mes }
    generacion.current++
    setBorrador(undefined)
    setCalculadoCon(undefined)
    setViendo(undefined)
    setErrorBorrador('')
    setErrorAccion('')
    setCampos(VACIOS)
    setPapeles([])
    setErrorDeLista('')
    if (!clienteId) return
    let vigente = true
    api.papelesDeIsr(clienteId, anio, mes)
      .then((lista) => vigente && setPapeles(lista))
      .catch((e) => vigente && setErrorDeLista(mensaje(e, 'No se pudieron leer los papeles sellados de este mes.')))
    return () => { vigente = false }
  }, [clienteId, anio, mes])

  // Los parámetros del ejercicio cambiaron: el borrador se calculó con los anteriores y ya no es lo
  // que quedaría sellado, ni el que todavía esté en vuelo.
  function descartarBorrador() {
    generacion.current++
    setBorrador(undefined)
    setCalculadoCon(undefined)
    setErrorBorrador('')
  }

  async function calcular() {
    // No depende solo del botón deshabilitado: con cambios sin guardar, el borrador saldría con otros parámetros que los que se ven.
    if (!clienteId || parametrosSucios) return
    const datos = adatos(campos)
    const g = ++generacion.current
    setErrorBorrador('')
    try {
      const calculado = await api.borradorDePapelDeIsr(clienteId, anio, mes, datos)
      if (g !== generacion.current) return
      setBorrador(calculado)
      setCalculadoCon(datos)
    } catch (e) {
      if (g !== generacion.current) return
      setBorrador(undefined)
      setCalculadoCon(undefined)
      setErrorBorrador(mensaje(e, 'No se pudo calcular el papel de trabajo.'))
    }
  }

  async function sellar() {
    if (!clienteId || !calculadoCon || !borrador) return
    const [a, m] = [anio, mes]
    setOcupado(true)
    setErrorAccion('')
    let sellado: Papel
    try {
      // Se sella con lo capturado que se vio en el borrador, no con lo que haya cambiado en el formulario
      // después, y contra la misma versión de los parámetros del ejercicio: si alguien la cambió, el
      // servidor se niega. Los ingresos sí se releen de la contabilidad al sellar.
      sellado = await api.sellarPapelDeIsr(clienteId, {
        anio: a, mes: m, ...calculadoCon, capturaDeParametrosId: borrador.capturaDeParametrosId,
      })
    } catch (e) {
      if (!sigueEnElPeriodo(a, m)) return
      setErrorAccion(e instanceof ErrorDeApi && e.estado === 403
        ? 'Tu rol no puede sellar papeles de trabajo: pide a un contador o a un administrador del despacho que lo haga.'
        : mensaje(e, 'No se pudo sellar el papel de trabajo.'))
      // Otro colaborador u otra pestaña cambió los parámetros: el borrador ya no vale y el panel relee los vigentes.
      if (e instanceof ErrorDeApi && e.codigo === 'PARAMETROS_CAMBIARON') {
        descartarBorrador()
        setRecargaDeParametros((n) => n + 1)
      }
      return
    } finally {
      setOcupado(false)
    }
    if (!sigueEnElPeriodo(a, m)) return
    // El papel ya quedó sellado: se muestra aunque la lista no se pueda recargar.
    setViendo(sellado)
    try {
      const lista = await api.papelesDeIsr(clienteId, a, m)
      if (!sigueEnElPeriodo(a, m)) return
      setPapeles(lista)
      setErrorDeLista('')
    } catch {
      if (!sigueEnElPeriodo(a, m)) return
      setErrorDeLista('El papel se selló, pero no se pudo recargar la lista de sellados: recarga la página para verlo en ella.')
    }
  }

  async function ver(papelId: string) {
    if (!clienteId) return
    const [a, m] = [anio, mes]
    setErrorAccion('')
    try {
      const abierto = await api.papelDeIsr(clienteId, papelId)
      if (!sigueEnElPeriodo(a, m)) return
      setViendo(abierto)
    } catch (e) {
      if (!sigueEnElPeriodo(a, m)) return
      setErrorAccion(mensaje(e, 'No se pudo abrir el papel de trabajo.'))
    }
  }

  async function exportar(papel: { id: string; anio: number; mes: number }, formato: 'excel' | 'pdf') {
    if (!clienteId) return
    setErrorAccion('')
    try {
      await api.exportarPapelDeIsr(clienteId, papel, formato)
    } catch (e) {
      setErrorAccion(mensaje(e, 'No se pudo exportar el papel de trabajo.'))
    }
  }

  if (!clienteId) return <p>Cargando…</p>

  const enPantalla = viendo ?? borrador
  const hayCambios = calculadoCon !== undefined && JSON.stringify(adatos(campos)) !== JSON.stringify(calculadoCon)

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente?.razonSocial ?? 'Expediente'}</Link></p>

      <header className="encabezado linea-base">
        <h1>Papel de trabajo de ISR provisional</h1>
        <select
          className="compacta"
          style={{ width: 'auto' }}
          aria-label="Periodo"
          value={`${anio}-${mes}`}
          onChange={(e) => {
            const [a, m] = e.target.value.split('-')
            setAnio(Number(a))
            setMes(Number(m))
          }}
        >
          {periodos.map((p) => (
            <option key={`${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>{nombreDePeriodo(p.anio, p.mes)}</option>
          ))}
        </select>
        {cliente && <span className="encabezado-sub mono">{cliente.rfc} · {cliente.razonSocial}</span>}
      </header>

      <PaqueteDeDeclaraciones clienteId={clienteId} anio={anio} mes={mes} />

      <p className="nota">
        Personas morales del régimen general (LISR art. 14). Los ingresos salen de la contabilidad —pólizas vigentes, del 1 de
        enero al cierre del mes— y solo cuenta lo contabilizado. El coeficiente, la PTU y las pérdidas salen de los parámetros
        del ejercicio; los pagos anteriores y las retenciones los capturas cada mes, cada cifra con su origen.
      </p>

      <ParametrosDeIsr key={recargaDeParametros} clienteId={clienteId} ejercicio={anio} mes={mes}
        alCambiarLosParametros={descartarBorrador} alCambiarSucio={setParametrosSucios} />

      <h2>Datos del mes</h2>

      <form
        aria-label="Datos del pago provisional"
        onSubmit={(e) => {
          e.preventDefault()
          void calcular()
        }}
      >
        {CIFRAS.map(({ campo, origen, etiqueta, etiquetaDelOrigen, ejemplo }) => (
          <div className="filtros" key={campo}>
            <label>
              {etiqueta}
              <input type="number" min="0" step="0.01" value={campos[campo]} placeholder="0.00"
                onChange={(e) => setCampos({ ...campos, [campo]: e.target.value })} />
            </label>
            <label>
              {etiquetaDelOrigen}
              <input value={campos[origen]} maxLength={300} placeholder={ejemplo}
                onChange={(e) => setCampos({ ...campos, [origen]: e.target.value })} />
            </label>
          </div>
        ))}
        <p className="filtros">
          {parametrosSucios && <span className="sutil">{AVISO_DE_PARAMETROS_SUCIOS}</span>}
          <button type="submit" className="primario" disabled={parametrosSucios}>Calcular</button>
        </p>
      </form>

      {errorBorrador && <p className="error" role="alert">{errorBorrador}</p>}
      {errorAccion && <p className="error" role="alert">{errorAccion}</p>}

      {enPantalla && (
        <section aria-labelledby="titulo-papel">
          <h2 id="titulo-papel">
            {viendo?.generadoEnUtc
              ? `Sellado el ${instante(viendo.generadoEnUtc)}`
              : `Borrador · ${nombreDePeriodo(anio, mes)}`}
            {viendo && <> · <button className="enlace" onClick={() => setViendo(undefined)}>Volver al borrador</button></>}
          </h2>

          {enPantalla.advertencias.length > 0 && (
            <ul className="aviso" role="status" aria-label="Advertencias">
              {enPantalla.advertencias.map((a, i) => <li key={`${a.codigo}-${i}`}>{a.mensaje}</li>)}
            </ul>
          )}

          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr><th scope="col">Concepto</th><th scope="col" className="num">Importe</th><th scope="col">Origen</th></tr>
              </thead>
              <tbody>
                {enPantalla.renglones.map((r) => (
                  <tr key={r.concepto}>
                    <th scope="row" style={RENGLONES_DE_RESULTADO.has(r.concepto) ? { fontWeight: 700 } : { fontWeight: 400 }}>
                      {r.etiqueta}
                    </th>
                    <td className="num">{dinero(r.importe)}</td>
                    <td className="sutil">{r.origen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {enPantalla.parametros.length > 0 && (
            <p className="nota">
              Tasa usada: {enPantalla.parametros.map((p) =>
                `${p.parametro} ${p.valor} (${p.origen}${p.cotejado ? '' : '; sin cotejar'})`).join(' · ')}
            </p>
          )}

          {!viendo && (
            <p className="filtros">
              {parametrosSucios
                ? <span className="sutil">{AVISO_DE_PARAMETROS_SUCIOS}</span>
                : hayCambios && <span className="sutil">Cambiaste lo que capturaste: recalcula antes de sellar.</span>}
              <button className="primario" disabled={ocupado || hayCambios || parametrosSucios} onClick={() => void sellar()}>
                {ocupado ? 'Sellando…' : 'Sellar papel de trabajo'}
              </button>
            </p>
          )}
        </section>
      )}

      <section aria-labelledby="titulo-sellados">
        <h2 id="titulo-sellados">Sellados de {nombreDePeriodo(anio, mes)}</h2>
        {errorDeLista && <p className="error" role="alert">{errorDeLista}</p>}
        {papeles.length === 0 ? (
          !errorDeLista && <p className="vacio">Aún no hay papeles sellados para este mes.</p>
        ) : (
          <div className="tarjeta"><ul className="lista-compacta">
            {papeles.map((p, i) => (
              <li key={p.id}>
                <span>
                  {instante(p.generadoEnUtc)}{i === 0 && ' · vigente'} · a pagar {dinero(p.impuestoAPagar)}
                  {p.pagosEnExceso > 0 && ` · pagos en exceso ${dinero(p.pagosEnExceso)}`}
                  {p.tieneAdvertencias && ' · con advertencias'}
                </span>
                <span className="acciones-de-fila">
                  <button className="enlace" onClick={() => void ver(p.id)}>Ver</button>
                  <button className="enlace" onClick={() => void exportar(p, 'excel')}>.xlsx</button>
                  <button className="enlace" onClick={() => void exportar(p, 'pdf')}>PDF</button>
                </span>
              </li>
            ))}
          </ul></div>
        )}
      </section>
    </>
  )
}
